"use client";

import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { cn } from "@/lib/utils";

const OtpErrorContext = React.createContext<boolean>(false);

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput> & { isError?: boolean }
>(({ className, containerClassName, isError, ...props }, ref) => (
  <OtpErrorContext.Provider value={Boolean(isError)}>
    <OTPInput
      ref={ref}
      containerClassName={cn(
        "flex items-center gap-3 disabled:cursor-not-allowed",
        containerClassName,
      )}
      className={cn("disabled:cursor-not-allowed", className)}
      aria-invalid={Boolean(isError)}
      {...props}
    />
  </OtpErrorContext.Provider>
));
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div"> & { isError?: boolean }
>(({ className, isError, ...props }, ref) => {
  const isErrorContext = React.useContext(OtpErrorContext);
  const effectiveError = isError ?? isErrorContext;

  return (
    <OtpErrorContext.Provider value={effectiveError}>
      <div
        ref={ref}
        className={cn("flex items-center gap-3", className)}
        {...props}
      />
    </OtpErrorContext.Provider>
  );
});
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div"> & { index: number; isError?: boolean }
>(({ index, isError: isErrorProp, className, ...props }, ref) => {
  const inputOtpContext = React.useContext(OTPInputContext);
  const isErrorContext = React.useContext(OtpErrorContext);
  const isError = isErrorProp ?? isErrorContext;
  const slot = inputOtpContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex h-14 w-12 items-center justify-center rounded-xl border text-[24px] font-semibold text-[#1C1E21] transition-colors",
        isError
          ? "border-error-foreground"
          : slot.isActive || slot.char
            ? "border-[#3BDAFF]"
            : "border-[#EDEEEE]",
        className,
      )}
      {...props}
    >
      {slot.char ?? ""}
      {slot.hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-6 w-px animate-caret-blink bg-[#1C1E21] duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

export { InputOTP, InputOTPGroup, InputOTPSlot };
